/**
 * V17.5: Notification Service
 *
 * Provides API integration for the Lead Lifecycle Notification system:
 * - Fetch notifications for current user
 * - Mark as read / Mark all as read
 * - Dismiss notifications
 * - Poll for new notifications
 */

import api from './axiosInstance';
import { formatAppDate } from '../utils/format';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export enum NotificationType {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
}

export enum NotificationCategory {
  SYSTEM = 'system',
  LEAD_ASSIGNMENT = 'lead_assignment',
  LEAD_RATING = 'lead_rating',
  LEAD_UPDATE = 'lead_update',  // V17.8: Lead info updated by scout
  SLA_WARNING = 'sla_warning',
  SLA_BREACH = 'sla_breach',
  DISCOUNT_APPROVAL = 'discount_approval',  // V17.16.72
  CONTRACT_EXPIRY = 'contract_expiry',  // V17.19.0
  SERVICE_DAYS_CONFIRMED = 'service_days_confirmed',  // V17.20
  UPSELL_OPPORTUNITY = 'upsell_opportunity',  // V17.32.1
  SITE_CHECK_COMPLETED = 'site_check_completed',  // V17.59
  TPO_REWORK = 'tpo_rework',  // V17.63: TPO sent account back for rework
  TPO_REJECTION = 'tpo_rejection',  // V17.63: TPO rejected account
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  // V17.28: Lao language support
  title_lao?: string | null;
  message_lao?: string | null;
  type: NotificationType;
  category: NotificationCategory;
  reference_link: string | null;
  is_read: boolean;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  unread_count: number;
  total_count: number;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get notifications for the current user
 *
 * V17.5: Uses unified /notifications endpoint accessible by ALL authenticated users
 * (not /scout/notifications which requires scout.lead.view permission)
 */
export async function getNotifications(
  limit: number = 50,
  includeDismissed: boolean = false
): Promise<NotificationListResponse> {
  const response = await api.get('/notifications', {
    params: {
      limit,
      include_dismissed: includeDismissed,
    },
  });
  return response.data;
}

/**
 * Mark a specific notification as read
 */
export async function markAsRead(notificationId: string): Promise<void> {
  await api.post(`/notifications/${notificationId}/read`);
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<{ message: string }> {
  const response = await api.post('/notifications/read-all');
  return response.data;
}

/**
 * Dismiss a notification (hide from list)
 */
export async function dismissNotification(notificationId: string): Promise<void> {
  await api.post(`/notifications/${notificationId}/dismiss`);
}

/**
 * Get only the unread count (lightweight poll)
 */
export async function getUnreadCount(): Promise<number> {
  const response = await api.get('/notifications', {
    params: { limit: 1 },
  });
  return response.data.unread_count;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get icon color class based on notification type
 */
export function getNotificationTypeColor(type: NotificationType): string {
  switch (type) {
    case NotificationType.SUCCESS:
      return 'text-green-500';
    case NotificationType.WARNING:
      return 'text-yellow-500';
    case NotificationType.ERROR:
      return 'text-red-500';
    default:
      return 'text-blue-500';
  }
}

/**
 * Get background color class based on notification type
 */
export function getNotificationTypeBg(type: NotificationType): string {
  switch (type) {
    case NotificationType.SUCCESS:
      return 'bg-green-50 border-green-200';
    case NotificationType.WARNING:
      return 'bg-yellow-50 border-yellow-200';
    case NotificationType.ERROR:
      return 'bg-red-50 border-red-200';
    default:
      return 'bg-blue-50 border-blue-200';
  }
}

/**
 * Brief 2026-10-08-001: notification times in Lao local time, in the reader's language.
 *
 * The server stores `created_at` as a naive UTC `utcnow()` (audit item B1 is the global fix). A
 * string with no offset is therefore UTC — read as local time it made a brand-new notice say
 * "7h ago". An explicit offset (the API now sends `+00:00`) is kept as is, so this never
 * double-shifts once B1 lands.
 */
export function parseNotificationTime(value: string): Date {
  const hasOffset = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasOffset ? value : `${value}Z`);
}

/** A time as `YYYY-MM-DD HH:mm` in Asia/Vientiane, whatever the browser's own zone is. */
/** A notification's date + time in Lao local time, in the reader's language (B2, brief 2026-10-11-001). */
export function formatNotificationDateTime(value: string, language: 'lo' | 'en' = 'lo'): string {
  return formatAppDate(parseNotificationTime(value), language);
}

/**
 * Relative time for a notification — "just now / N min / N h / N days ago" in Lao (ຫາກໍ່ / ນາທີ /
 * ຊົ່ວໂມງ / ມື້) or English; older than 7 days → date + time in Asia/Vientiane.
 * The bell and the /notifications page both use this.
 */
export function formatNotificationTime(
  dateString: string,
  language: 'lo' | 'en' = 'lo',
  now: Date = new Date()
): string {
  const date = parseNotificationTime(dateString);
  const diffMs = Math.max(0, now.getTime() - date.getTime());
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const lo = language === 'lo';

  if (diffMins < 1) return lo ? 'ຫາກໍ່' : 'Just now';
  if (diffMins < 60) return lo ? `${diffMins} ນາທີກ່ອນ` : `${diffMins} min ago`;
  if (diffHours < 24) return lo ? `${diffHours} ຊົ່ວໂມງກ່ອນ` : `${diffHours} h ago`;
  if (diffDays < 7) return lo ? `${diffDays} ມື້ກ່ອນ` : `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return formatNotificationDateTime(dateString, language);
}

/** The Lao title / message when Lao is selected and one exists; else the English text. */
export function localizedTitle(n: Notification, language: 'lo' | 'en'): string {
  return language === 'lo' && n.title_lao ? n.title_lao : n.title;
}

export function localizedMessage(n: Notification, language: 'lo' | 'en'): string {
  return language === 'lo' && n.message_lao ? n.message_lao : n.message;
}
